import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import ProblemSelectModal from '../components/TestRegister/ProblemSelectModal';
import BulkAddModal from '../components/TestRegister/BulkAddModal';
import BulkCopyModal from '../components/TestRegister/BulkCopyModal';
import TestRegisterPrintModal from '../components/TestRegister/TestRegisterPrintModal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function TestRegister({ treeId: treeIdProp, embedded = false, onBack }) {
  const { tree_id: tree_id_param } = useParams();
  const tree_id = treeIdProp || tree_id_param;
  const navigate = useNavigate();
  const { getAccessToken, handleApiError, userType } = useAuth();
  
  // 기본 state 선언
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [visibleDepth, setVisibleDepth] = useState(4);
  const [selectedPathInfo, setSelectedPathInfo] = useState(null);
  const [isDepthDropdownOpen, setIsDepthDropdownOpen] = useState(false);
  const depthDropdownRef = useRef(null);
  const mobileScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [showBulkCopyModal, setShowBulkCopyModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  const depthTitles = {
    1: '대단원',
    2: '소단원',
    3: '대유형',
    4: '중유형'
  };
  
  const columnTitles = {
    1: '대단원',
    2: '소단원',
    3: '대유형',
    4: '중유형',
    5: '소유형'
  };

  // fetchWithTokenRefresh 함수를 먼저 정의
  const fetchWithTokenRefresh = useCallback(async (endpoint, options = {}) => {
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': accessToken,
          'Content-Type': 'application/json',
        },
      });

      // 응답을 JSON으로 변환
      const data = await response.json();
      
      // 409를 포함한 모든 응답을 정상적으로 반환
      return data;

    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [getAccessToken, handleApiError]);

  // organizeDataByPathId 함수 정의
  const organizeDataByPathId = useCallback((treeData) => {
    if (!treeData || !treeData.depth1 || !treeData.depth2 || !treeData.depth3 || !treeData.depth4) return [];

    const organizedData = [];
    let prevD1 = null;
    let prevD2 = null;
    let prevD3 = null;
    let prevD4 = null;

    // depth1 데이터 순회
    treeData.depth1['Y-axis'].forEach(d1Item => {
      const d1PathId = d1Item[2];
      
      // depth2 항목들 찾기
      const d2Items = treeData.depth2['Y-axis'].filter(d2 => 
        d2[2].startsWith(d1PathId + '/')
      );

      d2Items.forEach(d2Item => {
        const d2PathId = d2Item[2];

        // depth3 항목들 찾기
        const d3Items = treeData.depth3['Y-axis'].filter(d3 => 
          d3[2].startsWith(d2PathId + '/')
        );

        d3Items.forEach(d3Item => {
          const d3PathId = d3Item[2];

          // depth4 항목들 찾기
          const d4Items = treeData.depth4['Y-axis'].filter(d4 => 
            d4[2].startsWith(d3PathId + '/')
          );

          d4Items.forEach(d4Item => {
            // 이전 값과 비교하여 같으면 빈 문자열, 다르면 새 값 할당
            const currentD1 = prevD1 === d1Item[1] ? '' : d1Item[1];
            const currentD2 = prevD2 === d2Item[1] ? '' : d2Item[1];
            const currentD3 = prevD3 === d3Item[1] ? '' : d3Item[1];
            const currentD4 = prevD4 === d4Item[1] ? '' : d4Item[1];

            organizedData.push({
              depth1: currentD1,
              depth2: currentD2,
              depth3: currentD3,
              depth4: currentD4,
              pathId: d4Item[2],
              steps: Array(5).fill('')
            });

            // 현재 값을 이전 값으로 저장
            prevD1 = d1Item[1];
            prevD2 = d2Item[1];
            prevD3 = d3Item[1];
            prevD4 = d4Item[1];
          });
        });
      });
    });

    return organizedData;
  }, []);

  const aggregateStepNumbers = useCallback((data, depth) => {
    const problems = treeData?.problems?.field_problems || {};
    const depthKey = `d${depth}_y_axis`;
    
    return data.map(row => {
      const pathParts = row.pathId.split('/');
      const currentPathId = pathParts.slice(0, depth + 1).join('/');
      
      // 해당 경로의 문제 수 배열 가져오기
      const stepCounts = problems[depthKey]?.[currentPathId] || Array(5).fill(0);
      
      return {
        ...row,
        steps: stepCounts
      };
    });
  }, [treeData]);

  // getFilteredData 수를 나중에 정의
  const getFilteredData = useCallback(() => {
    if (!treeData || !treeData.depth1 || !treeData.depth2 || !treeData.depth3 || !treeData.depth4) return [];
    
    // 현재 depth에 맞는 데이터 구성
    const uniqueRows = new Map();
    let prevD1 = '', prevD2 = '', prevD3 = '', prevD4 = '';
    
    // treeData의 현재 depth에 해당하는 데이터 사용
    const currentDepthData = {
      1: treeData.depth1['Y-axis'],
      2: treeData.depth2['Y-axis'],
      3: treeData.depth3['Y-axis'],
      4: treeData.depth4['Y-axis']
    }[visibleDepth];

    if (!currentDepthData) return [];

    // 현재 depth의 데이터를 기준으로 행 구성
    currentDepthData.forEach(item => {
      const pathParts = item[2].split('/');
      const currentDepthPath = pathParts.slice(0, visibleDepth + 1).join('/');
      
      if (!uniqueRows.has(currentDepthPath)) {
        const row = {
          pathId: currentDepthPath,
          steps: Array(5).fill(0)
        };

        // 각 depth별 데이터 찾아서 추가
        if (visibleDepth >= 1) {
          const depth1Path = pathParts.slice(0, 2).join('/');
          const depth1Item = treeData.depth1['Y-axis'].find(d1 => d1[2] === depth1Path);
          row.depth1 = depth1Item && depth1Item[1] !== prevD1 ? depth1Item[1] : '';
          prevD1 = depth1Item ? depth1Item[1] : prevD1;
        }
        
        if (visibleDepth >= 2) {
          const depth2Path = pathParts.slice(0, 3).join('/');
          const depth2Item = treeData.depth2['Y-axis'].find(d2 => d2[2] === depth2Path);
          row.depth2 = depth2Item && depth2Item[1] !== prevD2 ? depth2Item[1] : '';
          prevD2 = depth2Item ? depth2Item[1] : prevD2;
        }
        
        if (visibleDepth >= 3) {
          const depth3Path = pathParts.slice(0, 4).join('/');
          const depth3Item = treeData.depth3['Y-axis'].find(d3 => d3[2] === depth3Path);
          row.depth3 = depth3Item && depth3Item[1] !== prevD3 ? depth3Item[1] : '';
          prevD3 = depth3Item ? depth3Item[1] : prevD3;
        }
        
        if (visibleDepth >= 4) {
          const depth4Path = pathParts.slice(0, 5).join('/');
          const depth4Item = treeData.depth4['Y-axis'].find(d4 => d4[2] === depth4Path);
          row.depth4 = depth4Item && depth4Item[1] !== prevD4 ? depth4Item[1] : '';
          prevD4 = depth4Item ? depth4Item[1] : prevD4;
        }
        
        if (visibleDepth >= 5) {
          row.depth5 = item[1];
        }

        uniqueRows.set(currentDepthPath, row);
      }
    });

    const filteredData = Array.from(uniqueRows.values());
    
    // pathId 기준으로 정렬
    const sortedData = filteredData.sort((a, b) => {
      const partsA = a.pathId.split('/').map(Number);
      const partsB = b.pathId.split('/').map(Number);
      
      for (let i = 0; i < partsA.length; i++) {
        if (partsA[i] !== partsB[i]) {
          return partsA[i] - partsB[i];
        }
      }
      return 0;
    });

    // 문제 수 합산 적용
    return aggregateStepNumbers(sortedData, visibleDepth);
  }, [treeData, visibleDepth, aggregateStepNumbers]);

  // initializeTreeData 함수를 먼저 선언
  const initializeTreeData = useCallback(async (treeId) => {
    try {
      await fetchWithTokenRefresh(`/api/referencefields/${treeId}`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('트리 초기화 실패:', error);
      throw error;
    }
  }, [fetchWithTokenRefresh]);

  // fetchTreeData 함수를 나중에 선언
  const fetchTreeData = useCallback(async (treeId) => {
    if (!treeId) return;
    
    try {
      setLoading(true);
      
      const treeResponse = await fetchWithTokenRefresh(`/api/referencefields/${treeId}`);
      
      if (treeResponse.status === 'error' && treeResponse.status_code === 409) {
        alert('트리를 먼저 확정해주세요.');
        // 선택 상태와 스크롤 유지, 팝업만 표시
        setLoading(false);
        return;
      }

      if (treeResponse.status === 'success') {
        // 문제 수 정보를 새로운 엔드포인트에서 가져오기
        const countResponse = await fetchWithTokenRefresh(`/api/referencefields/${treeId}/count/problems`);
        setTreeData({
          ...treeResponse.data,
          problems: {
            total_problems: countResponse.data.total_problems,
            field_problems: countResponse.data.field_problems
          }
        });
        setError('');
      }
    } catch (error) {
      console.error('데이터 불러오기 실패:', error);
      setTreeData(null);
      setError('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithTokenRefresh, navigate]);

  // 선택된 트리 데이터 로드
  useEffect(() => {
    if (tree_id) {
      fetchTreeData(tree_id);
    }
  }, [tree_id, fetchTreeData]);
  
  // 문제 수만 업데이트하는 함수
  const fetchProblemCounts = useCallback(async () => {
    if (!tree_id) return;
    
    try {
      // 문제 수 정보만 가져오기
      const countResponse = await fetchWithTokenRefresh(`/api/referencefields/${tree_id}/count/problems`);
      
      if (countResponse?.data) {
        setTreeData(prevData => ({
          ...prevData,
          problems: {
            total_problems: countResponse.data.total_problems,
            field_problems: countResponse.data.field_problems
          }
        }));
      }
    } catch (error) {
      console.error('문제 수 업데이트 실패:', error);
    }
  }, [tree_id, fetchWithTokenRefresh]);
  
  // 자동 포커싱 기능 (모든 화면 크기에서 작동)
  useEffect(() => {
    if (mobileScrollRef.current) {
      setTimeout(() => {
        if (mobileScrollRef.current) {
          const table = mobileScrollRef.current.querySelector('table');
          if (table) {
            const headers = table.querySelectorAll('thead th');
            if (headers.length > 0) {
              // 선택된 depth에 따라 마지막 보이는 컬럼으로 스크롤
              // visibleDepth가 현재 보이는 컬럼 수와 같으므로, 마지막 컬럼 인덱스는 visibleDepth - 1
              const targetHeaderIndex = Math.min(visibleDepth - 1, headers.length - 1);
              
              if (targetHeaderIndex >= 0) {
                const targetHeader = headers[targetHeaderIndex];
                // 해당 컬럼을 좌측 끝에 정렬
                const scrollPosition = targetHeader.offsetLeft;
                
                mobileScrollRef.current.scrollTo({
                  left: scrollPosition,
                  behavior: 'smooth'
                });
              }
            }
          }
        }
      }, 100);
    }
  }, [visibleDepth, treeData]);

  // TruncatedText 컴포넌트 수정
  const TruncatedText = ({ text, pathId, showPathId = true }) => {
    // text가 없을 경우 빈 문자열로 처리
    const safeText = text || '';
    const formattedPathId = (pathId && showPathId) ? `(${pathId.replace(/\//g, '_')}) ` : '';
    const displayText = `${formattedPathId}${safeText}`;
    const truncated = displayText.length > 60 ? displayText.slice(0, 60) + '...' : displayText;
    
    return (
      <div className="group relative overflow-hidden whitespace-nowrap">
        <span className="whitespace-nowrap">{truncated}</span>
        {displayText.length > 60 && (
          <div className="absolute left-0 top-full z-10 mt-1 hidden max-w-xs whitespace-nowrap rounded bg-gray-800 p-2 text-white shadow-lg group-hover:block">
            {displayText}
          </div>
        )}
      </div>
    );
  };

  // 숫자 변경 핸들러 수정
  const handleNumberClick = useCallback((rowIndex, stepIndex, address) => {
    if (!tree_id) return;
    
    // 선택된 주소의 경로 정보 구성
    const pathParts = address.split('/');
    const depth1Item = treeData.depth1['Y-axis'].find(d1 => d1[2] === `${pathParts[0]}/${pathParts[1]}`);
    const depth2Item = treeData.depth2['Y-axis'].find(d2 => d2[2] === `${pathParts[0]}/${pathParts[1]}/${pathParts[2]}`);
    const depth3Item = treeData.depth3['Y-axis'].find(d3 => d3[2] === `${pathParts[0]}/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}`);
    const depth4Item = treeData.depth4['Y-axis'].find(d4 => d4[2] === `${pathParts[0]}/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}/${pathParts[4]}`);

    const pathInfo = {
      depth1: {
        name: depth1Item[1],
        path_id: depth1Item[2]
      },
      depth2: {
        name: depth2Item[1],
        path_id: depth2Item[2]
      },
      depth3: {
        name: depth3Item[1],
        path_id: depth3Item[2]
      },
      depth4: {
        name: depth4Item[1],
        path_id: depth4Item[2]
      }
    };

    setSelectedPathInfo(pathInfo);
    setSelectedAddress(address);
    setSelectedStep(stepIndex);
    setShowProblemModal(true);
  }, [tree_id, treeData]);

  // 문제 선택 핸들러
  const handleProblemSelect = async (problemId) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/referencefields/${tree_id}/problems`, {
        method: 'POST',
        body: JSON.stringify({
          problem_id: problemId,
          address: selectedAddress,
          step: selectedStep + 1
        })
      });

      if (response.status === 'success') {
        // 응답 메시지에 따른 분기 처리
        if (response.message === 'Field problem already exists at same address and step') {
          alert('문제가 이미 해당 위치에 등록되어 있습니다.');
        } else if (response.message === 'Field problem updated successfully') {
          const originInfo = response.data?.origin_address && response.data?.origin_step 
            ? `\n(이전 위치: ${response.data.origin_address}, 난이도 ${response.data.origin_step})`
            : '';
          alert(`문제 위치가 업데이트되었습니다.${originInfo}`);
        } else {
          // 새로 등록된 경우 (201 또는 기본 200)
          alert('문제가 성공적으로 등록되었습니다.');
        }
        // 문제 수 업데이트
        await fetchProblemCounts();
        return true; // 성공 시 true 반환
      } else if (response.status_code === 409) {
        alert('이미 등록된 문제입니다.');
        return false; // 409 에러 시 false 반환
      } else {
        alert('문제 등록에 실패했습니다.');
        return false;
      }
    } catch (error) {
      console.error('문제 등록 실패:', error);
      alert('문제 등록에 실패했습니다.');
      return false;
    }
  };

  // NumberCell 컴포넌트 수정
  const NumberCell = ({ number, onClick, isClickable }) => (
    <td className="p-1 text-center">
      <div className="flex justify-center">
        <button 
          className={`h-6 w-10 rounded text-xs font-medium transition-all ${
            number === 0 
              ? 'bg-gray-300 text-gray-600' 
              : 'bg-primary text-white hover:bg-primary/90'
          } ${
            !isClickable ? 'cursor-not-allowed opacity-70' : ''
          }`}
          onClick={isClickable ? onClick : undefined}
          disabled={!isClickable}
        >
          {number}
        </button>
      </div>
    </td>
  );

  // 문제 등록 함수 수정
  const registerProblem = async (problemId, address, step) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/referencefields/${tree_id}/problems`, {
        method: 'POST',
        body: JSON.stringify({
          problem_id: problemId,
          address: `${address}`,
          step: step + 1
        })
      });

      if (response && response.status === 'success') {
        // 응답 메시지에 따른 분기 처리
        if (response.message === 'Field problem already exists at same address and step') {
          alert('문제가 이미 해당 위치에 등록되어 있습니다.');
        } else if (response.message === 'Field problem updated successfully') {
          const originInfo = response.data?.origin_address && response.data?.origin_step 
            ? `\n(이전 위치: ${response.data.origin_address}, 난이도 ${response.data.origin_step})`
            : '';
          alert(`문제 위치가 업데이트되었습니다.${originInfo}`);
        } else {
          // 새로 등록된 경우 (201 또는 기본 200)
          alert('문제가 성공적으로 등록되었습니다.');
        }
        setShowProblemModal(false);
        
        // 문제 정보만 새로 받아오기
        const problemsResponse = await fetchWithTokenRefresh(`/api/referencefields/${tree_id}/problems`);
        
        // 400 에러 처리 (OCR 미확인 문제)
        if (problemsResponse?.status === 'error' && problemsResponse?.status_code === 400) {
          if (problemsResponse.message === 'Problem is not confirmed ocr') {
            alert('OCR이 확인되지 않은 문제가 있습니다. 문제 상태를 확인해주세요.');
          } else {
            alert(problemsResponse.message || '문제 정보를 불러올 수 없습니다.');
          }
        } else if (problemsResponse && problemsResponse.status === 'success') {
          setTreeData(prevData => ({
            ...prevData,
            problems: problemsResponse.data
          }));
        }
      }
    } catch (error) {
      console.error('문제 등록 실패:', error);
      alert('문제 등록에 실패했습니다.');
    }
  };

  // DepthButton 컴포넌트는 더 이상 사용하지 않음 (드롭다운으로 대체)

  const content = (
    <>
      <div className={embedded ? "w-full" : "min-h-screen w-full"}>
        {loading ? (
          <div className="py-4 text-center">로딩 중...</div>
        ) : treeData ? (
          <div className="rounded-3xl bg-white p-4 shadow">
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={embedded && onBack ? onBack : () => navigate(-1)}
                  className="mr-1 rounded-full p-1 hover:bg-gray-100 transition-colors"
                  title="트리 편집으로 돌아가기"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-xl font-bold">문제 등록</h3>
                <span className="text-sm text-gray-500">
                  - 총 <span className="text-primary font-bold">{treeData?.problems?.total_problems || 0}</span> 문제
                </span>
              </div>
              {userType === 'manager' && (
                <div className="flex gap-2">
                  <button 
                    onClick={() => setShowBulkAddModal(true)}
                    className="rounded-3xl bg-blue-500 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm text-white hover:bg-blue-600 whitespace-nowrap"
                  >
                    대규모 편성
                  </button>
                  <button
                    onClick={() => setShowBulkCopyModal(true)}
                    className="rounded-3xl bg-green-500 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm text-white hover:bg-green-600 whitespace-nowrap"
                  >
                    문제 편성 복사
                  </button>
                  <button
                    onClick={() => setShowPrintModal(true)}
                    className="rounded-3xl bg-purple-500 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm text-white hover:bg-purple-600 whitespace-nowrap"
                  >
                    인쇄
                  </button>
                </div>
              )}
            </div>
            <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
              {treeData?.field_info?.tree_name || ''}
              <span className="ml-2 text-sm text-gray-500">
                {treeData?.field_info?.edu_ver || ''}
              </span>
            </h1>
            
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="relative depth-dropdown-container">
                <button
                  ref={depthDropdownRef}
                  onClick={() => setIsDepthDropdownOpen(!isDepthDropdownOpen)}
                  className="flex h-6 items-center gap-2 rounded-3xl border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span>{depthTitles[visibleDepth]}</span>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isDepthDropdownOpen && depthDropdownRef.current && createPortal(
                  <div 
                    className="depth-dropdown-menu fixed rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5" 
                    style={{ 
                      zIndex: 999999,
                      top: depthDropdownRef.current.getBoundingClientRect().bottom + 4 + 'px',
                      left: depthDropdownRef.current.getBoundingClientRect().left + 'px',
                      width: '128px'
                    }}
                  >
                    <div className="py-1" role="menu">
                      {Object.entries(depthTitles).map(([depth, title]) => (
                        <button
                          key={depth}
                          onClick={() => {
                            setVisibleDepth(Number(depth));
                            setIsDepthDropdownOpen(false);
                          }}
                          className={`
                            block w-full px-4 py-2 text-left text-sm 
                            ${Number(depth) === visibleDepth ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}
                            hover:bg-gray-100 hover:text-gray-900
                          `}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            </div>
            
            {/* 통합 뷰 - 고정 레벨 컬럼 (데스크탑과 모바일 동일) */}
            <div className="mb-[400px] relative border-t border-b border-gray-200">
              <div className="flex">
                {/* 스크롤 가능한 좌측 영역 */}
                <div ref={mobileScrollRef} className="flex-1 overflow-x-auto">
                  <table className="text-sm border-collapse">
                    <thead>
                      <tr className="h-[36px]">
                        {visibleDepth >= 1 && <th className="px-2 text-left whitespace-nowrap h-[36px] align-middle"><span className="text-sm text-gray-900">대단원</span></th>}
                        {visibleDepth >= 2 && <th className="px-2 text-left whitespace-nowrap h-[36px] align-middle"><span className="text-sm text-gray-900">소단원</span></th>}
                        {visibleDepth >= 3 && <th className="px-2 text-left whitespace-nowrap h-[36px] align-middle"><span className="text-sm text-gray-900">대유형</span></th>}
                        {visibleDepth >= 4 && <th className="px-2 text-left whitespace-nowrap h-[36px] align-middle"><span className="text-sm text-gray-900">중유형</span></th>}
                        {visibleDepth >= 5 && <th className="px-2 text-left whitespace-nowrap h-[36px] align-middle"><span className="text-sm text-gray-900">소유형</span></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredData().map((row, rowIndex) => (
                        <tr key={rowIndex} className="relative transition-all duration-150 hover:z-10 hover:outline hover:outline-1 hover:outline-primary h-[40px]">
                          {visibleDepth >= 1 && (
                            <td className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              <TruncatedText 
                                text={row.depth1 || ''} 
                                pathId={row.pathId.split('/').slice(0, 2).join('/')}
                                showPathId={row.depth1 !== ''}
                              />
                            </td>
                          )}
                          {visibleDepth >= 2 && (
                            <td className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              <TruncatedText 
                                text={row.depth2 || ''} 
                                pathId={row.pathId.split('/').slice(0, 3).join('/')}
                                showPathId={row.depth2 !== ''}
                              />
                            </td>
                          )}
                          {visibleDepth >= 3 && (
                            <td className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              <TruncatedText 
                                text={row.depth3 || ''} 
                                pathId={row.pathId.split('/').slice(0, 4).join('/')}
                                showPathId={row.depth3 !== ''}
                              />
                            </td>
                          )}
                          {visibleDepth >= 4 && (
                            <td className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              <TruncatedText 
                                text={row.depth4 || ''} 
                                pathId={row.pathId.split('/').slice(0, 5).join('/')}
                                showPathId={row.depth4 !== ''}
                              />
                            </td>
                          )}
                          {visibleDepth >= 5 && (
                            <td className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              <TruncatedText text={row.depth5 || ''} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* 고정된 우측 레벨 컬럼 영역 */}
                <div className="sticky right-0 bg-white border-l border-gray-200 shadow-sm">
                  <table className="text-sm border-collapse">
                    <thead>
                      <tr className="h-[36px]">
                        {[1, 2, 3, 4, 5].map(level => (
                          <th key={level} className="w-10 p-1 text-center h-[36px] align-middle">
                            <span className="text-gray-600">
                              <span className="text-xs">Lv</span>
                              <span className="text-sm ml-0.5">{level}</span>
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredData().map((row, rowIndex) => (
                        <tr key={rowIndex} className="h-[40px]">
                          {[0, 1, 2, 3, 4].map((stepIndex) => (
                            <td key={stepIndex} className="p-1 text-center h-[40px]">
                              <div className="flex justify-center items-center h-full">
                                <button 
                                  className={`h-6 w-8 rounded text-xs font-medium transition-all ${
                                    row.steps[stepIndex] === 0 
                                      ? 'bg-gray-300 text-gray-600' 
                                      : 'bg-primary text-white hover:bg-primary/90'
                                  } ${
                                    visibleDepth < 4 ? 'cursor-not-allowed opacity-70' : ''
                                  }`}
                                  onClick={visibleDepth >= 4 ? () => handleNumberClick(rowIndex, stepIndex, row.pathId) : undefined}
                                  disabled={visibleDepth < 4}
                                >
                                  {row.steps[stepIndex]}
                                </button>
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-4 text-center">데이터를 불러올 수 없습니다.</div>
        )}
      </div>
      
      {showProblemModal && (
        <ProblemSelectModal
          onClose={() => setShowProblemModal(false)}
          selectedTree={tree_id}
          selectedAddress={selectedAddress}
          selectedStep={selectedStep}
          pathInfo={selectedPathInfo}
          fetchTreeData={fetchTreeData}
          fetchProblemCounts={fetchProblemCounts}
          fetchWithTokenRefresh={fetchWithTokenRefresh}
          handleProblemSelect={handleProblemSelect}
        />
      )}
      
      {showBulkAddModal && (
        <BulkAddModal
          isOpen={showBulkAddModal}
          onClose={() => setShowBulkAddModal(false)}
          treeId={tree_id}
          fetchWithTokenRefresh={fetchWithTokenRefresh}
          fetchProblemCounts={fetchProblemCounts}
        />
      )}
      
      {showBulkCopyModal && (
        <BulkCopyModal
          isOpen={showBulkCopyModal}
          onClose={() => setShowBulkCopyModal(false)}
          targetTreeId={tree_id}
          fetchWithTokenRefresh={fetchWithTokenRefresh}
          fetchProblemCounts={fetchProblemCounts}
        />
      )}

      {showPrintModal && (
        <TestRegisterPrintModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          treeId={tree_id}
          treeData={treeData}
          fetchWithTokenRefresh={fetchWithTokenRefresh}
        />
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
}

export default TestRegister;